/**
 * Organizations API
 *
 * Handles all organization-related API calls.
 */

import { BaseApiClient } from '../base';
import { API_ENDPOINTS } from '../endpoints';
import type { OrganizationMetricsResponse } from '../types';
import type {
  UspacyPlacementLocale,
  UspacyProcessingCommand,
  UspacyProcessingCommandResult,
  UspacyProcessingStateResponse,
} from '@aica/api-types';

// Both sides of this endpoint are pinned by the same Zod schemas
// (`packages/api-types/src/schemas/uspacy.ts`), so a field the route stops
// sending stops compiling here.
export type {
  UspacyProcessingCommand,
  UspacyProcessingCommandResult,
  UspacyProcessingStateResponse,
};

export class OrganizationsApi extends BaseApiClient {
  async getUspacyProcessingState(
    orgId: string,
    language?: UspacyPlacementLocale
  ): Promise<UspacyProcessingStateResponse> {
    return this.request<UspacyProcessingStateResponse>(
      `/api/v1/organizations/${orgId}/uspacy/processing-state${
        language ? `?lang=${encodeURIComponent(language)}` : ''
      }`
    );
  }

  async setUspacyProcessingState(
    orgId: string,
    command: UspacyProcessingCommand
  ): Promise<UspacyProcessingCommandResult> {
    return this.request(`/api/v1/organizations/${orgId}/uspacy/processing-state`, {
      method: 'POST',
      body: JSON.stringify(command),
    });
  }

  async getOrganizationMetrics(
    orgId: string,
    params?: { range?: string; periodStart?: string; periodEnd?: string }
  ): Promise<OrganizationMetricsResponse> {
    const searchParams = new URLSearchParams();
    if (params?.range) searchParams.set('range', params.range);
    if (params?.periodStart) searchParams.set('periodStart', params.periodStart);
    if (params?.periodEnd) searchParams.set('periodEnd', params.periodEnd);
    const query = searchParams.toString();
    return this.request<OrganizationMetricsResponse>(
      `${API_ENDPOINTS.ORGANIZATIONS_METRICS(orgId)}${query ? `?${query}` : ''}`
    );
  }
}
